import { memo } from 'react';
import { Box, Card, CardContent, Typography, CircularProgress } from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from 'recharts';
import { useSubscriptionStats } from '../../hooks/subscriptions';

const COLORS = ['#6366F1', '#8B5CF6', '#3B82F6', '#10B981', '#F59E0B', '#EF4444'];

export const PlanDistributionChart = memo(() => {
  const { data: stats, isLoading } = useSubscriptionStats();

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  const planData = stats?.organizationsByPlan || [];

  // Transform data for pie chart
  const chartData = planData.map((item) => ({
    name: item.planName,
    value: item.count,
  }));

  const totalOrgs = chartData.reduce((sum, item) => sum + item.value, 0);

  // Custom label to show percentage
  const renderLabel = (entry: any) => {
    const percent = ((entry.value / totalOrgs) * 100).toFixed(0);
    return `${percent}%`;
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Organizations by Plan
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Distribution of organizations across subscription plans
        </Typography>

        <Box sx={{ width: '100%', height: 300, mt: 2 }}>
          {chartData.length === 0 ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100%"
            >
              <Typography variant="body2" color="text.secondary">
                No plan distribution data available
              </Typography>
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={renderLabel}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: number, name: string) => [
                    `${value} organizations`,
                    name,
                  ]}
                />
                <Legend
                  verticalAlign="bottom"
                  height={36}
                  iconType="circle"
                />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Box>

        {totalOrgs > 0 && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Total: <strong>{totalOrgs}</strong> organizations
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
});
